import { Dialog } from "@base-ui-components/react/dialog"
import cx from "classnames"
import { PanelRightOpen, X } from "lucide-react"
import { observer } from "mobx-react"
import { useRef } from "react"
import { Z_INDEX } from "../../constants"
import { useMediaQuery } from "../../hooks/useMediaQuery"
import { getTrayConfig } from "../../modules/registry"
import type { TrayManager } from "../../store/managers/TrayManager"

interface TaskTraySurfaceProps {
    tray: TrayManager
    workspaceSidebarExpanded?: boolean
}

/**
 * A task-local tool surface. Its fixed root owns the stacking layer so the
 * entire dialog clears the composer while remaining below the task sidebar.
 */
export const TaskTraySurface = observer(function TaskTraySurface({ tray, workspaceSidebarExpanded = true }: TaskTraySurfaceProps): JSX.Element | null {
    const compact = useMediaQuery("(max-width: 719px)")
    const surfaceRootRef = useRef<HTMLDivElement>(null)
    const config = tray.openTray ? getTrayConfig(tray.openTray) : null
    const renderedAsModal = tray.isModalOpen || (compact && tray.isDocked)
    if (!config?.renderContent || !renderedAsModal) return null
    const edgeToEdge = compact && config.id === "gitlog"

    return (
        <>
            <div ref={surfaceRootRef} className="pointer-events-none fixed inset-0" style={{ zIndex: Z_INDEX.TASK_TRAY_ROOT }} data-task-tray-root="true" />
            <Dialog.Root
                open
                modal={false}
                onOpenChange={(open, eventDetails) => {
                    if (open) return
                    if (eventDetails.reason === "outside-press") {
                        const target = eventDetails.event.target
                        if (target instanceof Element && target.closest('[data-task-workspace-sidebar="true"]')) {
                            eventDetails.cancel()
                            return
                        }
                    }
                    tray.close()
                }}
            >
                <Dialog.Portal container={surfaceRootRef}>
                    <Dialog.Backdrop
                        className="pointer-events-auto absolute inset-0 bg-black/25 backdrop-blur-[3px] transition-opacity duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0"
                        data-task-tray-backdrop="true"
                    />
                    <Dialog.Popup
                        className={cx(
                            "pointer-events-auto absolute flex min-h-0 flex-col overflow-hidden bg-base-100 text-base-content outline-none transition-[transform,opacity] duration-200 ease-out data-[ending-style]:translate-x-8 data-[ending-style]:opacity-0 data-[starting-style]:translate-x-8 data-[starting-style]:opacity-0 motion-reduce:transition-none",
                            edgeToEdge
                                ? "inset-0"
                                : "bottom-[clamp(1rem,3vh,2rem)] left-[clamp(1rem,2vw,2rem)] top-[clamp(1rem,3vh,2rem)] border border-border shadow-[0_28px_90px_rgba(0,0,0,0.38),0_8px_28px_rgba(0,0,0,0.2)]"
                        )}
                        style={
                            edgeToEdge
                                ? undefined
                                : {
                                      right: compact
                                          ? "clamp(1rem, 2vw, 2rem)"
                                          : `calc(${workspaceSidebarExpanded ? "18rem" : "3rem"} + clamp(1rem, 2vw, 2rem))`,
                                  }
                        }
                        initialFocus={false}
                        aria-label={config.compactLabel ?? config.label}
                        data-edge-to-edge={edgeToEdge || undefined}
                    >
                        <header className="flex h-11 shrink-0 items-center gap-3 border-b border-base-content/20 bg-base-200/90 px-4">
                            <Dialog.Title className="min-w-0 flex-1 truncate text-xs font-semibold">{config.compactLabel ?? config.label}</Dialog.Title>
                            {!compact && (
                                <button
                                    type="button"
                                    className="btn flex h-7 w-7 shrink-0 items-center justify-center border border-transparent text-muted transition-colors hover:border-border hover:bg-base-100 hover:text-base-content"
                                    onClick={() => tray.dock()}
                                    aria-label={`Dock ${config.compactLabel ?? config.label} to the right`}
                                    title="Dock to the right"
                                >
                                    <PanelRightOpen size={15} />
                                </button>
                            )}
                            <Dialog.Close
                                className="btn flex h-7 w-7 shrink-0 items-center justify-center border border-transparent text-muted transition-colors hover:border-border hover:bg-base-100 hover:text-base-content"
                                aria-label={`Close ${config.compactLabel ?? config.label}`}
                            >
                                <X size={15} />
                            </Dialog.Close>
                        </header>
                        <div className="min-h-0 flex-1 overflow-hidden bg-base-100">{config.renderContent(tray, { compact })}</div>
                    </Dialog.Popup>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    )
})
